import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, CircularProgress, Switch, Snackbar, Alert as MuiAlert,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Radio, RadioGroup, FormControlLabel, Typography,
  TextField, Paper, Container,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import {
  createEvent, createEventSeries, updateEvent, deleteEvent,
  fetchEventById, fetchEventTypesForUser, uploadEventImage,
} from '../utils/eventApi';
import {
  getPlanLimitUserFacing,
  PLAN_LIMIT_UPGRADE_PATH,
  PLAN_LIMIT_USER_MESSAGE,
} from '../utils/billingApi';
import UpgradePrompt from '../components/AlertComponents/UpgradePrompt';
import { useUser } from '../context/UserContext';
import { fetchUserContext } from '../utils/utils';
import { theme } from '../theme/colors';
import DeleteConfirmModal from '../components/AlertComponents/DeleteConfirmModal';
import AudienceSettings from '../components/AudienceSettings';
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
import RepeatIcon from '@mui/icons-material/Repeat';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { canEditEvent, formatRecurrenceText } from '../utils/eventEditAccess';
import { parseEventCoordinates } from '../utils/eventLocationUtils';
import EventTypeChipBar from '../components/EventsComponents/EventTypeChipBar';
import { canonicalEventTypeName, filterCanonicalEventTypes } from '../utils/eventTypeConfig';
import '../styles/component_styles/Events.scss';
import '../styles/component_styles/Campaigns.scss';

const GOOGLE_PLACES_API_KEY =
  process.env.REACT_APP_GOOGLE_PLACES_API_KEY ||
  process.env.REACT_PUBLIC_GOOGLE_PLACES_API_KEY ||
  process.env.REACT_APP_GOOGLE_CIVIC_API_KEY ||
  '';

/** http(s) URL with a real hostname (e.g. example.com, zoom.us), not just a scheme prefix. */
function isValidEventJoinUrl(raw) {
  const s = (raw || '').trim();
  if (!s) return true;
  if (!/^https?:\/\//i.test(s)) return false;
  let u;
  try {
    u = new URL(s);
  } catch {
    return false;
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
  const host = u.hostname;
  if (!host) return false;
  const h = host.toLowerCase();
  if (h === 'localhost' || /^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  return h.includes('.');
}

const VOLUNTEERS_NEEDED_MAX_CHARS = 4;

/** Digits only, capped length for volunteer headcount input. */
function sanitizeVolunteersNeededInput(raw) {
  return String(raw ?? '')
    .replace(/\D/g, '')
    .slice(0, VOLUNTEERS_NEEDED_MAX_CHARS);
}

// Event tags are intentionally commented out for now.

const RECURRENCE_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 Weeks' },
  { value: 'monthly', label: 'Monthly' },
];

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
  { value: 'America/Phoenix', label: 'Arizona (MST)' },
];

function getNextHour() {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d;
}

function toLocalDatetimeStr(d) {
  if (!d) return '';
  const dt = new Date(d);
  dt.setMinutes(dt.getMinutes() - dt.getTimezoneOffset());
  return dt.toISOString().slice(0, 16);
}

export default function CreateEventPage() {
  const muiTheme = useTheme();
  const { eventId } = useParams();
  const isEditing = !!eventId;
  const navigate = useNavigate();
  const location = useLocation();
  const duplicateFrom = location.state?.duplicateFrom ?? null;
  const { user, loadingUser, canCreateEvent, accountTypeName } = useUser();
  const fileInputRef = useRef(null);

  const [loadingPage, setLoadingPage] = useState(isEditing);
  const [editLoadFailed, setEditLoadFailed] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [upgradePrompt, setUpgradePrompt] = useState({
    open: false,
    message: '',
    upgradeUrl: '/account-details#billing',
  });

  // Delete / edit scope modal
  const [showScopeModal, setShowScopeModal] = useState(false);
  const [scopeType, setScopeType] = useState('edit'); // 'edit' | 'delete'
  const [scopeValue, setScopeValue] = useState('this_only');
  const [originalEvent, setOriginalEvent] = useState(null);

  // ── Form fields ──
  const defaultStart = getNextHour();
  const defaultEnd = new Date(defaultStart.getTime() + 3600000);

  const [selectedEventType, setSelectedEventType] = useState(
    duplicateFrom?.event_type?.id?.toString() || duplicateFrom?.event_type_id?.toString() || ''
  );
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDatetime, setStartDatetime] = useState(toLocalDatetimeStr(defaultStart));
  const [endDatetime, setEndDatetime] = useState(toLocalDatetimeStr(defaultEnd));
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York');
  const [isVirtual, setIsVirtual] = useState(false);
  const [virtualLink, setVirtualLink] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [address, setAddress] = useState('');
  const [venueName, setVenueName] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [locationMode, setLocationMode] = useState(GOOGLE_PLACES_API_KEY ? 'search' : 'manual');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [visibility, setVisibility] = useState('public');
  const [geoTarget, setGeoTarget] = useState('everyone');
  const [needsVolunteers, setNeedsVolunteers] = useState(false);
  const [volunteersNeededCount, setVolunteersNeededCount] = useState('');
  const [volunteerDescription, setVolunteerDescription] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [canCreateRecurringSeries, setCanCreateRecurringSeries] = useState(true);
  const [recurrenceFrequency, setRecurrenceFrequency] = useState('weekly');
  const [recurrenceEndType, setRecurrenceEndType] = useState('count');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [recurrenceCount, setRecurrenceCount] = useState('4');
  const [coverImageUrl, setCoverImageUrl] = useState(
    () => duplicateFrom?.cover_image || duplicateFrom?.cover_image_url || ''
  );
  const [uploadingImage, setUploadingImage] = useState(false);

  // Follow-up steps (match mobile: max 5, each has title, description, actionLink)
  const [hasFollowups, setHasFollowups] = useState(false);
  const [allowOptIn, setAllowOptIn] = useState(false);
  const [followupSteps, setFollowupSteps] = useState([]);

  // Confirm modals replacing window.confirm()
  const [showDeleteEventModal, setShowDeleteEventModal] = useState(false);
  const [removeStepIdx, setRemoveStepIdx] = useState(null);

  // Redirect users who cannot host events (e.g. incomplete account setup)
  useEffect(() => {
    if (loadingUser || !user || isEditing) return;
    if (!canCreateEvent && !accountTypeName) {
      setToast({
        open: true,
        message: 'Complete account setup to create events.',
        severity: 'warning',
      });
      setTimeout(() => navigate('/events'), 2000);
    }
  }, [user, loadingUser, canCreateEvent, accountTypeName, isEditing, navigate]);

  // Load event types
  useEffect(() => {
    fetchEventTypesForUser()
      .then(setEventTypes)
      .catch(() => {})
      .finally(() => setLoadingTypes(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    fetchUserContext().then((ctx) => {
      if (cancelled || !ctx?.entitlements) return;
      if (ctx.entitlements.can_create_recurring_series === false) {
        setCanCreateRecurringSeries(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleRecurringToggle = (checked) => {
    if (checked && !canCreateRecurringSeries) {
      setUpgradePrompt({
        open: true,
        message: PLAN_LIMIT_USER_MESSAGE,
        upgradeUrl: PLAN_LIMIT_UPGRADE_PATH,
      });
      return;
    }
    setIsRecurring(checked);
  };

  // Default to first allowed type on create (match create-post defaulting to Idea).
  useEffect(() => {
    const visibleTypes = filterCanonicalEventTypes(eventTypes);
    if (!visibleTypes.length || selectedEventType || isEditing) return;
    const hasDuplicateType =
      duplicateFrom?.event_type?.id || duplicateFrom?.event_type_id;
    if (hasDuplicateType) return;
    setSelectedEventType(String(visibleTypes[0].id));
  }, [eventTypes, selectedEventType, isEditing, duplicateFrom]);

  // Ensure duplicate/edit event type resolves to an allowed event type.
  useEffect(() => {
    const visibleTypes = filterCanonicalEventTypes(eventTypes);
    if (!visibleTypes.length || !selectedEventType) return;
    const exists = visibleTypes.some((type) => String(type.id) === String(selectedEventType));
    if (exists) return;

    const duplicateTypeName =
      duplicateFrom?.event_type?.name ||
      duplicateFrom?.event_type_name ||
      originalEvent?.event_type?.name;
    const duplicateDisplayName =
      duplicateFrom?.event_type?.display_name ||
      duplicateFrom?.event_type_display_name ||
      originalEvent?.event_type?.display_name;
    const duplicateCanonical = duplicateTypeName ? canonicalEventTypeName(duplicateTypeName) : '';
    const fallbackType = visibleTypes.find((type) => {
      if (duplicateTypeName && type.name === duplicateTypeName) return true;
      if (duplicateCanonical && canonicalEventTypeName(type.name) === duplicateCanonical) return true;
      if (duplicateDisplayName && type.display_name === duplicateDisplayName) return true;
      return false;
    });
    setSelectedEventType(fallbackType ? String(fallbackType.id) : String(visibleTypes[0]?.id || ''));
  }, [eventTypes, selectedEventType, duplicateFrom, originalEvent]);

  // Load event for editing
  useEffect(() => {
    if (!eventId) return;
    (async () => {
      try {
        const ev = await fetchEventById(eventId);
        if (!canEditEvent(ev)) {
          setToast({ open: true, message: 'This event has already started and can no longer be edited.', severity: 'error' });
          navigate(`/events/${eventId}`);
          return;
        }
        setOriginalEvent(ev);
        if (ev.event_type?.id) setSelectedEventType(ev.event_type.id);
        else if (ev.event_type_id) setSelectedEventType(ev.event_type_id);
        setTitle(ev.title || '');
        setDescription(ev.description || '');
        if (ev.start_datetime) setStartDatetime(toLocalDatetimeStr(ev.start_datetime));
        if (ev.end_datetime) setEndDatetime(toLocalDatetimeStr(ev.end_datetime));
        if (ev.timezone) setTimezone(ev.timezone);
        setIsVirtual(!!ev.is_virtual);
        setVirtualLink(ev.join_url || '');
        setCity(ev.city || '');
        setState(ev.state || '');
        setAddress(ev.address || '');
        setVenueName(ev.location_name || '');
        const { latitude: eventLat, longitude: eventLng } = parseEventCoordinates(ev);
        setLatitude(eventLat);
        setLongitude(eventLng);
        const hasInPersonLocation =
          !!(ev.location_name || ev.address || ev.city || ev.state);
        if (!ev.is_virtual && hasInPersonLocation) {
          setLocationMode('manual');
          setSelectedPlace(null);
        } else if (!ev.is_virtual && GOOGLE_PLACES_API_KEY) {
          setLocationMode('search');
          setSelectedPlace(null);
        }
        setVisibility(ev.visibility || 'public');
        setGeoTarget(ev.geo_target || 'everyone');
        setNeedsVolunteers(!!ev.needs_volunteers);
        setVolunteersNeededCount(sanitizeVolunteersNeededInput(ev.volunteers_needed_count ?? ''));
        setVolunteerDescription(ev.volunteer_description || '');
        setCoverImageUrl(ev.cover_image || ev.cover_image_url || '');
        if (ev.is_recurring) {
          setIsRecurring(true);
          setRecurrenceFrequency(ev.recurrence_rule || 'weekly');
        }
        if (ev.followup_steps?.length) {
          setHasFollowups(true);
          setAllowOptIn(!!ev.allow_opt_in_followups);
          setFollowupSteps(ev.followup_steps.map((s, i) => ({
            _key: (s.id || i).toString() + Date.now().toString(),
            id: s.id,
            title: s.title || '',
            description: s.description || '',
            actionLink: s.action_link || '',
          })));
        }
      } catch {
        setEditLoadFailed(true);
        setToast({ open: true, message: "We couldn't load this event. Returning to Events.", severity: 'error' });
        setTimeout(() => navigate('/events'), 2000);
      } finally {
        setLoadingPage(false);
      }
    })();
  }, [eventId, navigate]);

  // Prefill form when duplicating from a past event
  useEffect(() => {
    if (!duplicateFrom || isEditing) return;
    const ev = duplicateFrom;
    // selectedEventType is already initialized from duplicateFrom in useState
    setTitle(ev.title || '');
    setDescription(ev.description || '');
    // Dates intentionally left at defaults — past dates would fail validation
    if (ev.timezone) setTimezone(ev.timezone);
    setIsVirtual(!!ev.is_virtual);
    setVirtualLink(ev.join_url || '');
    setCity(ev.city || '');
    setState(ev.state || '');
    setAddress(ev.address || '');
    setVenueName(ev.location_name || '');
    const { latitude: eventLat, longitude: eventLng } = parseEventCoordinates(ev);
    setLatitude(eventLat);
    setLongitude(eventLng);
    if (!ev.is_virtual && (ev.location_name || ev.address || ev.city || ev.state)) {
      setLocationMode('manual');
      setSelectedPlace(null);
    }
    setVisibility(ev.visibility || 'public');
    setGeoTarget(ev.geo_target || 'everyone');
    setNeedsVolunteers(!!ev.needs_volunteers);
    setVolunteersNeededCount(sanitizeVolunteersNeededInput(ev.volunteers_needed_count ?? ''));
    setVolunteerDescription(ev.volunteer_description || '');
    setCoverImageUrl(ev.cover_image || ev.cover_image_url || '');
    // Copy follow-up steps content but strip IDs (these are new steps)
    if (ev.followup_steps?.length) {
      setHasFollowups(true);
      setAllowOptIn(!!ev.allow_opt_in_followups);
      setFollowupSteps(ev.followup_steps.map((s, i) => ({
        _key: i.toString() + Date.now().toString(),
        id: null,
        title: s.title || '',
        description: s.description || '',
        actionLink: s.action_link || '',
      })));
    }
    // Don't copy recurrence — the duplicate is always a fresh one-time event
  }, [duplicateFrom]); // eslint-disable-line react-hooks/exhaustive-deps

  const parseAddressComponents = useCallback((result) => {
    const components = result?.address_components || [];
    let streetNumber = '';
    let route = '';
    let cityPart = '';
    let statePart = '';
    let name = '';
    components.forEach((c) => {
      const t = c.types;
      if (t.includes('street_number')) streetNumber = c.long_name;
      else if (t.includes('route')) route = c.long_name;
      else if (t.includes('locality') || t.includes('sublocality') || t.includes('neighborhood')) {
        if (!cityPart) cityPart = c.long_name;
      } else if (t.includes('administrative_area_level_1')) statePart = c.short_name;
    });
    const addrLine = streetNumber && route ? `${streetNumber} ${route}` : route || streetNumber;
    if (result?.name && result.name !== addrLine) name = result.name;
    return {
      location_name: name,
      location_city: cityPart,
      location_state: statePart,
      location_address: addrLine || result?.formatted_address || '',
    };
  }, []);

  const handlePlaceSelect = useCallback(
    async (placeOption) => {
      setSelectedPlace(placeOption);
      if (!placeOption?.value?.place_id) {
        setVenueName('');
        setAddress('');
        setCity('');
        setState('');
        setLatitude(null);
        setLongitude(null);
        return;
      }
      try {
        const results = await geocodeByPlaceId(placeOption.value.place_id);
        const parsed = parseAddressComponents(results?.[0]);
        if (!parsed.location_name) parsed.location_name = placeOption.label || '';
        setVenueName(parsed.location_name || '');
        setAddress(parsed.location_address || '');
        setCity(parsed.location_city || '');
        setState(parsed.location_state || '');
        // Capture coordinates from the geocode result so events appear on the map
        // without an extra backend geocoding call.
        const loc = results?.[0]?.geometry?.location;
        if (loc) {
          const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
          const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
          setLatitude(typeof lat === 'number' ? lat : null);
          setLongitude(typeof lng === 'number' ? lng : null);
        } else {
          setLatitude(null);
          setLongitude(null);
        }
      } catch {
        setVenueName(placeOption.label || '');
        setAddress('');
        setCity('');
        setState('');
        setLatitude(null);
        setLongitude(null);
      }
    },
    [parseAddressComponents],
  );

  // ── Image upload ──
  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10 MB

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setErrors((prev) => ({ ...prev, coverImage: 'Unsupported file type. Please upload a JPEG, PNG, WebP, or GIF image.' }));
      e.target.value = '';
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      setErrors((prev) => ({ ...prev, coverImage: `Image is too large (${sizeMB} MB). Maximum size is 10 MB.` }));
      e.target.value = '';
      return;
    }

    setUploadingImage(true);
    setErrors((prev) => { const next = { ...prev }; delete next.coverImage; return next; });
    try {
      const res = await uploadEventImage(file);
      setCoverImageUrl(res.image_url || res.url);
      setErrors((prev) => { const next = { ...prev }; delete next.coverImage; return next; });
    } catch (err) {
      const message = err?.message || '';
      let userMessage = message || 'Image upload failed. Please try again.';
      if (!message) userMessage = 'Image upload failed. Please try again.';
      else if (message.includes('401') || message.includes('403')) userMessage = 'You must be logged in to upload images.';
      setErrors((prev) => ({ ...prev, coverImage: userMessage }));
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  // ── Follow-up helpers (match mobile: max 5 steps, reorder support) ──
  const addFollowupStep = () => {
    if (followupSteps.length >= 5) return;
    setFollowupSteps((s) => [
      ...s,
      { _key: Date.now().toString() + Math.random().toString(36).slice(2), title: '', description: '', actionLink: '' },
    ]);
  };
  const removeFollowupStep = (idx) => setFollowupSteps((s) => s.filter((_, i) => i !== idx));
  const updateFollowupStep = (idx, field, value) =>
    setFollowupSteps((s) => s.map((step, i) => (i === idx ? { ...step, [field]: value } : step)));
  const moveFollowupStep = (idx, direction) => {
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === followupSteps.length - 1) return;
    setFollowupSteps((prev) => {
      const updated = [...prev];
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
      return updated;
    });
  };

  // ── Tag toggle ──
  // ── Validation ──
  const validate = () => {
    const e = {};
    if (!selectedEventType) e.eventType = 'Event type is required.';
    if (!coverImageUrl) e.coverImage = 'A cover image is required.';
    if (!title.trim() || title.length < 5 || title.length > 255) e.title = 'Title must be between 5 and 255 characters.';
    if (!startDatetime) e.startDatetime = 'Start date/time is required';
    if (!endDatetime) e.endDatetime = 'End date/time is required';
    if (startDatetime && endDatetime && new Date(startDatetime) >= new Date(endDatetime))
      e.endDatetime = 'End must be after start';
    if (isVirtual && virtualLink.trim() && !isValidEventJoinUrl(virtualLink))
      e.virtualLink =
        'Enter a full URL with http:// or https:// and a domain (e.g. https://example.com/… or https://zoom.us/…).';
    if (!isVirtual && !city.trim()) e.city = 'City is required for in-person events';
    if (!isVirtual && !state.trim()) e.state = 'State is required for in-person events';
    if (needsVolunteers) {
      const count = parseInt(volunteersNeededCount, 10);
      if (!volunteersNeededCount.trim() || isNaN(count) || count < 1)
        e.volunteersNeededCount = 'Please enter a valid number of volunteers needed (at least 1).';
    }
    if (hasFollowups) {
      if (followupSteps.length === 0) e.followupSteps = 'At least one follow-up step is required.';
      if (followupSteps.length > 5) e.followupSteps = 'Maximum 5 follow-up steps allowed.';
      followupSteps.forEach((step, i) => {
        if (!step.title || !step.title.trim()) e[`followupStep_${i}_title`] = 'Step title is required.';
        if (step.description && step.description.length > 4000)
          e[`followupStep_${i}_description`] = 'Step description must be 4000 characters or less.';
        if (step.actionLink && step.actionLink.trim() && !isValidEventJoinUrl(step.actionLink.trim()))
          e[`followupStep_${i}_actionLink`] = 'Enter a full URL with http:// or https:// and a domain (e.g. https://example.com/…).';
      });
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Submit ──
  const handleSubmit = async (editScope) => {
    if (isEditing && (!originalEvent || editLoadFailed)) return;
    if (!validate()) return;
    setIsSubmitting(true);

    const payload = {
      event_type_id: selectedEventType,
      title: title.trim(),
      description: description.trim(),
      start_datetime: new Date(startDatetime).toISOString(),
      end_datetime: new Date(endDatetime).toISOString(),
      timezone,
      is_virtual: isVirtual,
      join_url: isVirtual ? virtualLink : '',
      city: isVirtual ? '' : city.trim(),
      state: isVirtual ? '' : state.trim(),
      address: isVirtual ? '' : address.trim(),
      location_name: isVirtual ? '' : venueName.trim(),
      latitude: isVirtual ? null : (latitude != null ? Number(latitude) : null),
      longitude: isVirtual ? null : (longitude != null ? Number(longitude) : null),
      visibility,
      geo_target: geoTarget,
      needs_volunteers: needsVolunteers,
      volunteers_needed_count: needsVolunteers ? Number(volunteersNeededCount) || 0 : 0,
      volunteer_description: needsVolunteers ? volunteerDescription : '',
      cover_image: coverImageUrl || undefined,
      ...(hasFollowups && followupSteps.length > 0
        ? {
            allow_opt_in_followups: allowOptIn,
            followup_steps: followupSteps.map((s, i) => ({
              step_order: i + 1,
              title: s.title.trim(),
              description: s.description.trim() || null,
              image_url: null,
              action_link: s.actionLink.trim() || null,
            })),
          }
        : { allow_opt_in_followups: false }),
    };

    try {
      if (isEditing) {
        await updateEvent(eventId, payload, editScope || 'this_only');
        setToast({ open: true, message: 'Event updated!', severity: 'success' });
        setTimeout(() => navigate(`/events/${eventId}`), 1000);
      } else if (isRecurring) {
        const seriesData = {
          ...payload,
          recurrence_rule: recurrenceFrequency,
          ...(recurrenceEndType === 'date' && recurrenceEndDate
            ? { recurrence_end_date: new Date(recurrenceEndDate).toISOString() }
            : {}),
          ...(recurrenceEndType === 'count' ? { recurrence_count: Number(recurrenceCount) || 4 } : {}),
        };
        await createEventSeries(seriesData);
        setToast({ open: true, message: 'Recurring event series created!', severity: 'success' });
        setTimeout(() => navigate('/events'), 1000);
      } else {
        await createEvent(payload);
        setToast({ open: true, message: 'Event created!', severity: 'success' });
        setTimeout(() => navigate('/events'), 1000);
      }
    } catch (err) {
      const planLimit = getPlanLimitUserFacing(err);
      if (planLimit) {
        setUpgradePrompt({
          open: true,
          message: planLimit.message,
          upgradeUrl: planLimit.upgradeHref,
        });
      } else {
        const msg = err?.response?.data?.error || err.message || 'Failed to save event.';
        setToast({ open: true, message: msg, severity: 'error' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Delete ──
  const handleDelete = async (scope) => {
    if (isEditing && (!originalEvent || editLoadFailed)) return;
    try {
      await deleteEvent(eventId, scope || 'this_only');
      setToast({ open: true, message: 'Event deleted.', severity: 'success' });
      setTimeout(() => navigate('/events'), 1000);
    } catch {
      setToast({ open: true, message: 'Failed to delete event.', severity: 'error' });
    }
  };

  if (loadingPage) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={40} sx={{ color: theme.primary }} />
      </Box>
    );
  }

  if (editLoadFailed) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
        <MuiAlert severity="error" variant="filled" sx={{ borderRadius: 2, maxWidth: 420 }}>
          We couldn't load this event. Returning to Events.
        </MuiAlert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: 4 }}>
      <Container maxWidth="sm" className="create-event">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>
            {isEditing ? 'Edit Event' : 'Create Event'}
          </Typography>
          <Button
            onClick={() => navigate(isEditing ? `/events/${eventId}` : '/events')}
            sx={{ textTransform: 'none', color: '#64748b', fontWeight: 600 }}
          >
            Cancel
          </Button>
        </Box>

        {duplicateFrom && !isEditing && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, mb: 2, px: 2, py: 1.5,
            borderRadius: '12px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0',
          }}>
            <ContentCopyOutlinedIcon sx={{ fontSize: 18, color: '#0f172a' }} />
            <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 500 }}>
              Content copied from <strong>{duplicateFrom.title}</strong>. Update the date and time, then save as a new event.
            </Typography>
          </Box>
        )}

        {isEditing && originalEvent?.is_recurring && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, mb: 2, px: 2, py: 1.5,
            borderRadius: '12px', backgroundColor: `${theme.primary}08`, border: `1px solid ${theme.primary}25`,
          }}>
            <RepeatIcon sx={{ fontSize: 20, color: theme.primary }} />
            <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 500 }}>
              This is a recurring event ({formatRecurrenceText(originalEvent) || 'Recurring'})
            </Typography>
          </Box>
        )}

        <EventTypeChipBar
          eventTypes={eventTypes}
          value={selectedEventType}
          onChange={setSelectedEventType}
          loading={loadingTypes}
        />
        {errors.eventType && (
          <Typography sx={{ fontSize: 13, color: '#ef4444', mb: 2, mt: -1 }}>
            {errors.eventType}
          </Typography>
        )}

        <div className="create-event__form">
        {/* Cover image */}
        <div className="create-event__field">
          <label className="create-event__label">
            Cover Image <span className="create-event__required-star">*</span>
          </label>
          {coverImageUrl && <img src={coverImageUrl} alt="" className="create-event__image-preview" />}
          <div
            className="create-event__image-upload"
            onClick={() => fileInputRef.current?.click()}
            style={errors.coverImage ? { borderColor: '#dc2626' } : {}}
          >
            {uploadingImage ? (
              <CircularProgress size={24} sx={{ color: theme.primary }} />
            ) : (
              <>
                <CloudUploadIcon sx={{ fontSize: 32 }} />
                <span>{coverImageUrl ? 'Change image' : 'Upload cover image'}</span>
              </>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={handleImageUpload} />
          {errors.coverImage && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8,
              padding: '10px 14px', backgroundColor: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 8, color: '#991b1b', fontSize: 14, lineHeight: 1.5,
            }}>
              <span style={{ fontSize: 18, lineHeight: 1 }}>⚠</span>
              <span>{errors.coverImage}</span>
            </div>
          )}
        </div>

        {/* Title */}
        <div className="create-event__field">
          <label className="create-event__label">
            Title <span className="create-event__required-star">*</span>
          </label>
          <input className="create-event__input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Give your event a name" maxLength={255} />
          {errors.title && <span className="create-event__error">{errors.title}</span>}
        </div>

        {/* Description */}
        <div className="create-event__field">
          <label className="create-event__label">Description</label>
          <textarea className="create-event__textarea" value={description} onChange={(e) => { if (e.target.value.length <= 2000) setDescription(e.target.value); }} placeholder="Tell attendees what this event is about..." rows={5} />
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.25, color: description.length >= 2000 ? '#dc2626' : '#64748b' }}>
            {description.length}/2000
          </Typography>
        </div>

        {/* Date/Time */}
        <div className="create-event__row">
          <div className="create-event__field">
            <label className="create-event__label">
              Start <span className="create-event__required-star">*</span>
            </label>
            <input className="create-event__input" type="datetime-local" value={startDatetime} onChange={(e) => setStartDatetime(e.target.value)} />
            {errors.startDatetime && <span className="create-event__error">{errors.startDatetime}</span>}
          </div>
          <div className="create-event__field">
            <label className="create-event__label">
              End <span className="create-event__required-star">*</span>
            </label>
            <input className="create-event__input" type="datetime-local" value={endDatetime} onChange={(e) => setEndDatetime(e.target.value)} />
            {errors.endDatetime && <span className="create-event__error">{errors.endDatetime}</span>}
          </div>
        </div>

        {/* Timezone */}
        <div className="create-event__field">
          <label className="create-event__label">Timezone</label>
          <select className="events-filters__select" value={timezone} onChange={(e) => setTimezone(e.target.value)} style={{ width: '100%' }}>
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>

        {/* Location (match Create Town Hall post styling) */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: '14px',
            border: '1px solid #e5e7eb',
            bgcolor: '#ffffff',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.5 }}>
            <LocationOnOutlinedIcon sx={{ fontSize: 22, color: theme.primary, mt: 0.25 }} />
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>
                Location
              </Typography>
              <Typography sx={{ fontSize: 14, color: '#0f172a', mt: 0.5, lineHeight: 1.45 }}>
                Virtual or in-person
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              py: 2,
              mb: 2,
              borderBottom: '1px solid #f1f5f9',
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>Virtual event</Typography>
              <Typography sx={{ fontSize: 13, color: '#64748b', mt: 0.25 }}>Attendees join online</Typography>
            </Box>
            <Switch
              checked={isVirtual}
              onChange={(e) => {
                const next = e.target.checked;
                setIsVirtual(next);
                if (next) {
                  setSelectedPlace(null);
                } else if (GOOGLE_PLACES_API_KEY) {
                  setLocationMode('search');
                }
              }}
              sx={{
                '& .Mui-checked': { color: theme.primary },
                '& .Mui-checked + .MuiSwitch-track': { backgroundColor: theme.primary },
              }}
            />
          </Box>

          {isVirtual ? (
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75 }}>
                Join link
              </Typography>
              <TextField
                fullWidth
                placeholder="https://…"
                value={virtualLink}
                onChange={(e) => setVirtualLink(e.target.value)}
                error={!!errors.virtualLink}
                helperText={errors.virtualLink}
                FormHelperTextProps={{ sx: { mx: 0, mt: 0.75 } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    bgcolor: '#fff',
                    '& fieldset': { borderColor: '#e5e7eb' },
                    '&:hover fieldset': { borderColor: '#d1d5db' },
                    '&.Mui-focused fieldset': { borderColor: theme.primary, borderWidth: 1 },
                  },
                }}
              />
            </Box>
          ) : (
            <>
              {GOOGLE_PLACES_API_KEY && (
                <Box
                  role="group"
                  aria-label="How to enter location"
                  sx={{
                    display: 'flex',
                    p: '4px',
                    borderRadius: '12px',
                    bgcolor: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                    gap: '4px',
                    mb: 2,
                  }}
                >
                  {[
                    { id: 'search', label: 'Search' },
                    { id: 'manual', label: 'Enter manually' },
                  ].map(({ id, label }) => {
                    const active = locationMode === id;
                    return (
                      <Button
                        key={id}
                        disableElevation
                        onClick={() => setLocationMode(id)}
                        sx={{
                          flex: 1,
                          py: 1,
                          px: 1.5,
                          minHeight: 40,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: 13,
                          borderRadius: '10px',
                          boxShadow: active ? '0 1px 3px rgba(15, 23, 42, 0.08)' : 'none',
                          bgcolor: active ? '#ffffff' : 'transparent',
                          color: active ? theme.primary : '#64748b',
                          border: active ? `1px solid ${theme.primary}33` : '1px solid transparent',
                          '&:hover': {
                            bgcolor: active ? '#ffffff' : 'rgba(255,255,255,0.6)',
                            color: active ? theme.primary : '#334155',
                          },
                        }}
                      >
                        {label}
                      </Button>
                    );
                  })}
                </Box>
              )}

              {locationMode === 'search' && GOOGLE_PLACES_API_KEY ? (
                <Box>
                  <GooglePlacesAutocomplete
                    apiKey={GOOGLE_PLACES_API_KEY}
                    autocompletionRequest={{ componentRestrictions: { country: 'us' } }}
                    debounce={300}
                    selectProps={{
                      value: selectedPlace,
                      onChange: handlePlaceSelect,
                      placeholder: 'Start typing an address or place name…',
                      isClearable: true,
                      styles: {
                        control: (provided, state) => ({
                          ...provided,
                          minHeight: 48,
                          borderRadius: 12,
                          borderColor: state.isFocused ? theme.primary : '#e5e7eb',
                          boxShadow: state.isFocused ? `0 0 0 1px ${theme.primary}` : 'none',
                          '&:hover': { borderColor: state.isFocused ? theme.primary : '#d1d5db' },
                        }),
                        placeholder: (provided) => ({ ...provided, color: '#9ca3af' }),
                        menu: (provided) => ({ ...provided, zIndex: 60, borderRadius: 12, overflow: 'hidden' }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isFocused ? '#f8fafc' : '#fff',
                          color: '#111827',
                          paddingTop: 10,
                          paddingBottom: 10,
                        }),
                      },
                    }}
                  />
                  {(venueName || address || city || state) && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        borderRadius: '12px',
                        bgcolor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '10px',
                          bgcolor: `${theme.primary}14`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <LocationOnOutlinedIcon sx={{ fontSize: 20, color: theme.primary }} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>
                          Selected location
                        </Typography>
                        {venueName && (
                          <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#0f172a', mb: 0.25 }}>{venueName}</Typography>
                        )}
                        <Typography sx={{ fontSize: 14, color: '#334155', lineHeight: 1.45 }}>
                          {[address, city, state].filter(Boolean).join(', ')}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.25 }}>
                    Address details
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Venue or place name"
                      placeholder="e.g. City Hall, Community Center"
                      value={venueName}
                      onChange={(e) => setVenueName(e.target.value)}
                      helperText="Optional — add if the venue has a name."
                      FormHelperTextProps={{ sx: { color: '#64748b', mx: 0, mt: 0.75 } }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#fff', '& fieldset': { borderColor: '#e5e7eb' } } }}
                    />
                    <TextField
                      fullWidth
                      label="Street address"
                      placeholder="123 Main Street"
                      value={address}
                      onChange={(e) => { setAddress(e.target.value); setLatitude(null); setLongitude(null); }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#fff', '& fieldset': { borderColor: '#e5e7eb' } } }}
                    />
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <TextField
                        fullWidth
                        required
                        label="City"
                        placeholder="City"
                        value={city}
                        onChange={(e) => { setCity(e.target.value); setLatitude(null); setLongitude(null); }}
                        error={!!errors.city}
                        helperText={errors.city}
                        FormHelperTextProps={{ sx: { mx: 0, mt: 0.75 } }}
                        sx={{
                          flex: '1 1 160px',
                          minWidth: 140,
                          '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#fff', '& fieldset': { borderColor: '#e5e7eb' } },
                          '& .MuiFormLabel-asterisk': { color: '#ef4444', fontSize: '1.25em', fontWeight: 600 },
                        }}
                      />
                      <TextField
                        required
                        label="State"
                        placeholder="NY"
                        value={state}
                        onChange={(e) => { setState(e.target.value.toUpperCase().slice(0, 2)); setLatitude(null); setLongitude(null); }}
                        error={!!errors.state}
                        helperText={errors.state}
                        FormHelperTextProps={{ sx: { mx: 0, mt: 0.75 } }}
                        sx={{
                          flex: '0 0 96px',
                          width: 96,
                          '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#fff', '& fieldset': { borderColor: '#e5e7eb' } },
                          '& .MuiFormLabel-asterisk': { color: '#ef4444', fontSize: '1.25em', fontWeight: 600 },
                        }}
                        inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Paper>

        {/* MVP: Event tags commented out for now until they are part of discovery/filtering. */}

        {/* Recurring */}
        {!isEditing && (
          <>
            <div className="create-event__switch-row">
              <div>
                <div className="create-event__switch-label">Recurring Event</div>
                <div className="create-event__switch-sublabel">Repeat on a schedule</div>
              </div>
              <Switch checked={isRecurring} onChange={(e) => handleRecurringToggle(e.target.checked)} sx={{ '& .Mui-checked': { color: theme.primary }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: theme.primary } }} />
            </div>
            {isRecurring && (
              <div className="create-event__row">
                <div className="create-event__field">
                  <label className="create-event__label">Frequency</label>
                  <select className="events-filters__select" value={recurrenceFrequency} onChange={(e) => setRecurrenceFrequency(e.target.value)} style={{ width: '100%' }}>
                    {RECURRENCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="create-event__field">
                  <label className="create-event__label">End After</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select className="events-filters__select" value={recurrenceEndType} onChange={(e) => setRecurrenceEndType(e.target.value)}>
                      <option value="count">Occurrences</option>
                      <option value="date">Date</option>
                    </select>
                    {recurrenceEndType === 'count' ? (
                      <input className="create-event__input" type="number" value={recurrenceCount} onChange={(e) => setRecurrenceCount(e.target.value)} min={1} max={52} style={{ width: 80 }} />
                    ) : (
                      <input className="create-event__input" type="date" value={recurrenceEndDate} onChange={(e) => setRecurrenceEndDate(e.target.value)} />
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <AudienceSettings
          visibility={visibility}
          onVisibilityChange={setVisibility}
          geoTarget={geoTarget}
          onGeoTargetChange={setGeoTarget}
          showGeoTarget
          contentLabel="event"
        />

        {/* Volunteers */}
        <div className="create-event__switch-row">
          <div>
            <div className="create-event__switch-label">Need Volunteers</div>
            <div className="create-event__switch-sublabel">Allow attendees to sign up as volunteers</div>
          </div>
          <Switch checked={needsVolunteers} onChange={(e) => setNeedsVolunteers(e.target.checked)} sx={{ '& .Mui-checked': { color: theme.primary }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: theme.primary } }} />
        </div>
        {needsVolunteers && (
          <>
            <div className="create-event__field" style={{ width: '120px', marginBottom: '16px' }}>
              <label className="create-event__label">How many?</label>
              <input
                className={`create-event__input${errors.volunteersNeededCount ? ' create-event__input--error' : ''}`}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={VOLUNTEERS_NEEDED_MAX_CHARS}
                value={volunteersNeededCount}
                onChange={(e) => setVolunteersNeededCount(sanitizeVolunteersNeededInput(e.target.value))}
                placeholder="e.g. 5"
              />
              {errors.volunteersNeededCount && (
                <Typography variant="body2" sx={{ color: '#dc2626', fontSize: 12, mt: 0.25 }}>{errors.volunteersNeededCount}</Typography>
              )}
            </div>
            <div className="create-event__field">
              <label className="create-event__label">Volunteer role description</label>
              <textarea
                className="create-event__input"
                value={volunteerDescription}
                onChange={(e) => { if (e.target.value.length <= 500) setVolunteerDescription(e.target.value); }}
                placeholder="What will volunteers do?"
                rows={3}
                style={{ resize: 'vertical' }}
              />
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.25, color: volunteerDescription.length >= 500 ? '#dc2626' : '#64748b' }}>
                {volunteerDescription.length}/500
              </Typography>
            </div>
          </>
        )}

        {/* Follow-up steps */}
        <div className="create-event__switch-row">
          <div>
            <div className="create-event__switch-label">Follow-up Actions</div>
            <div className="create-event__switch-sublabel">Give attendees post-event action items</div>
          </div>
          <Switch checked={hasFollowups} onChange={(e) => setHasFollowups(e.target.checked)} sx={{ '& .Mui-checked': { color: theme.primary }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: theme.primary } }} />
        </div>
        {hasFollowups && (
          <>
            {/* QR check-in info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: '#f0fdf4', borderRadius: '8px', mb: 1 }}>
              <QrCode2Icon sx={{ fontSize: 20, color: theme.primary }} />
              <Typography variant="body2" sx={{ color: '#0f172a' }}>
                Check-in attendees will always receive follow-up steps on their dashboard
              </Typography>
            </Box>

            {/* Opt-in toggle */}
            <div className="create-event__switch-row" style={{ borderBottom: 'none', paddingBottom: 0 }}>
              <div>
                <div className="create-event__switch-label">Allow non-checked-in people to opt in to follow-up steps</div>
              </div>
              <Switch checked={allowOptIn} onChange={(e) => setAllowOptIn(e.target.checked)} sx={{ '& .Mui-checked': { color: theme.primary }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: theme.primary } }} />
            </div> 

            {/* General follow-up error */}
            {errors.followupSteps && (
              <Typography variant="body2" sx={{ color: '#dc2626', px: 2, mb: 1 }}>
                {errors.followupSteps}
              </Typography>
            )}

            {/* Steps header */}
            {followupSteps.length > 0 && (
              <Box sx={{ px: 2, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  Follow-Up Steps ({followupSteps.length}/5)
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Add actionable steps for attendees to complete after the event
                </Typography>
              </Box>
            )}

            {/* Step cards */}
            {followupSteps.map((step, idx) => (
              <Box
                key={step._key || idx}
                sx={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  p: 2,
                  mb: 1.5,
                  bgcolor: '#fff',
                }}
              >
                {/* Step header with number badge + reorder/remove */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 28, height: 28, borderRadius: '50%', bgcolor: theme.primary,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>{idx + 1}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>Step {idx + 1}</Typography>
                      {step.title && (
                        <Typography sx={{ fontSize: 12, color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {step.title}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {idx > 0 && (
                      <Button
                        size="small"
                        onClick={() => moveFollowupStep(idx, 'up')}
                        sx={{
                          minWidth: 0, p: 0.5, color: theme.primary,
                          backgroundColor: 'transparent',
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                        }}
                      >
                        <ArrowUpwardIcon sx={{ fontSize: 18 }} />
                      </Button>
                    )}
                    {idx < followupSteps.length - 1 && (
                      <Button
                        size="small"
                        onClick={() => moveFollowupStep(idx, 'down')}
                        sx={{
                          minWidth: 0, p: 0.5, color: theme.primary,
                          backgroundColor: 'transparent',
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                        }}
                      >
                        <ArrowDownwardIcon sx={{ fontSize: 18 }} />
                      </Button>
                    )}
                    <Button
                      size="small"
                      onClick={() => setRemoveStepIdx(idx)}
                      sx={{
                        minWidth: 0, p: 0.5, color: '#0f172a',
                        backgroundColor: 'transparent',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </Button>
                  </Box>
                </Box>

                {/* Title field */}
                <div className="create-event__field">
                  <label className="create-event__label">
                    Title <span className="create-event__required-star">*</span>
                  </label>
                  <input
                    className={`create-event__input${errors[`followupStep_${idx}_title`] ? ' create-event__input--error' : ''}`}
                    value={step.title}
                    onChange={(e) => updateFollowupStep(idx, 'title', e.target.value)}
                    placeholder="e.g., Contact Your Representative"
                    maxLength={255}
                  />
                  {errors[`followupStep_${idx}_title`] && (
                    <Typography variant="body2" sx={{ color: '#dc2626', fontSize: 12, mt: 0.25 }}>
                      {errors[`followupStep_${idx}_title`]}
                    </Typography>
                  )}
                </div>

                {/* Description field */}
                <div className="create-event__field" style={{ marginTop: 8 }}>
                  <label className="create-event__label">Description</label>
                  <textarea
                    className={`create-event__textarea${errors[`followupStep_${idx}_description`] ? ' create-event__input--error' : ''}`}
                    value={step.description}
                    onChange={(e) => updateFollowupStep(idx, 'description', e.target.value)}
                    placeholder="Describe the steps attendees should take..."
                    rows={3}
                    maxLength={4000}
                    style={{ minHeight: 80 }}
                  />
                  {errors[`followupStep_${idx}_description`] && (
                    <Typography variant="body2" sx={{ color: '#dc2626', fontSize: 12, mt: 0.25 }}>
                      {errors[`followupStep_${idx}_description`]}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ color: '#0f172a', fontSize: 12, mt: 0.25, textAlign: 'right' }}>
                    {step.description?.length || 0}/4000 characters
                  </Typography>
                </div>

                {/* Action link field */}
                <div className="create-event__field" style={{ marginTop: 8 }}>
                  <label className="create-event__label">Action Link</label>
                  <input
                    className={`create-event__input${errors[`followupStep_${idx}_actionLink`] ? ' create-event__input--error' : ''}`}
                    value={step.actionLink}
                    onChange={(e) => updateFollowupStep(idx, 'actionLink', e.target.value)}
                    placeholder="https://example.com/action"
                  />
                  {errors[`followupStep_${idx}_actionLink`] && (
                    <Typography variant="body2" sx={{ color: '#dc2626', fontSize: 12, mt: 0.25 }}>
                      {errors[`followupStep_${idx}_actionLink`]}
                    </Typography>
                  )}
                </div>
              </Box>
            ))}

            {/* Add step button */}
            {followupSteps.length < 5 && (
              <Box
                onClick={addFollowupStep}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  border: '2px dashed #d1d5db', borderRadius: '12px',
                  p: 1.5, cursor: 'pointer', justifyContent: 'center',
                  '&:hover': { borderColor: theme.primary, bgcolor: '#f0fdf4' },
                }}
              >
                <Box sx={{
                  width: 28, height: 28, borderRadius: '50%', bgcolor: '#f0fdf4',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <AddCircleOutlineIcon sx={{ fontSize: 18, color: theme.primary }} />
                </Box>
                <Typography sx={{ color: theme.primary, fontWeight: 600, fontSize: 14 }}>
                  Add Step {followupSteps.length + 1} ({followupSteps.length + 1}/5)
                </Typography>
              </Box>
            )}

            {/* Max reached message */}
            {followupSteps.length >= 5 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2, py: 1 }}>
                <CheckCircleIcon sx={{ fontSize: 18, color: '#0f172a' }} />
                <Typography variant="body2" sx={{ color: '#0f172a' }}>
                  Maximum of 5 steps reached
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Submit */}
        <div className="create-event__submit-row">
          {isEditing && (
            <button
              className="create-event__cancel-btn"
              style={{ color: '#dc2626', borderColor: '#fca5a5' }}
              onClick={() => {
                if (originalEvent?.is_recurring) {
                  setScopeType('delete');
                  setShowScopeModal(true);
                } else {
                  setShowDeleteEventModal(true);
                }
              }}
            >
              <DeleteIcon sx={{ fontSize: 16, mr: 0.5 }} /> Delete
            </button>
          )}
          <button className="create-event__cancel-btn" onClick={() => navigate(isEditing ? `/events/${eventId}` : '/events')}>
            Cancel
          </button>
          <button
            className="create-event__submit-btn"
            disabled={isSubmitting}
            onClick={() => {
              if (isEditing && originalEvent?.is_recurring) {
                setScopeType('edit');
                setShowScopeModal(true);
              } else {
                handleSubmit();
              }
            }}
          >
            {isSubmitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : isEditing ? 'Save Changes' : isRecurring ? 'Create Series' : 'Create Event'}
          </button>
        </div>
      </div>

      {/* ── Scope modal (edit/delete recurring) ── */}
      <Dialog open={showScopeModal} onClose={() => setShowScopeModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{scopeType === 'delete' ? 'Delete Scope' : 'Edit Scope'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#0f172a' }}>
            This is a recurring event. What would you like to {scopeType}?
          </Typography>
          <RadioGroup value={scopeValue} onChange={(e) => setScopeValue(e.target.value)}>
            <FormControlLabel value="this_only" control={<Radio sx={{ color: theme.primary, '&.Mui-checked': { color: theme.primary } }} />} label="Just this event" />
            <FormControlLabel value="all_future" control={<Radio sx={{ color: theme.primary, '&.Mui-checked': { color: theme.primary } }} />} label="This and all future events" />
            <FormControlLabel
              value="entire_series"
              control={<Radio sx={{ color: theme.primary, '&.Mui-checked': { color: theme.primary } }} />}
              label={scopeType === 'delete' ? 'Entire series' : 'Entire series (template + future events)'}
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowScopeModal(false)}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: '#0f172a',
              borderColor: '#0f172a',
              backgroundColor: '#fff',
              boxShadow: muiTheme.shadows[2],
              '&:hover': {
                borderColor: '#0f172a',
                backgroundColor: 'rgba(15, 23, 42, 0.06)',
                boxShadow: muiTheme.shadows[2],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowScopeModal(false);
              if (scopeType === 'delete') handleDelete(scopeValue);
              else handleSubmit(scopeValue);
            }}
            sx={{
              background: scopeType === 'delete' ? '#dc2626' : theme.gradient,
              boxShadow: muiTheme.shadows[2],
              '&:hover': {
                background: scopeType === 'delete' ? '#b91c1c' : theme.gradient,
                opacity: 0.92,
                boxShadow: muiTheme.shadows[2],
              },
            }}
          >
            {scopeType === 'delete' ? 'Delete' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmModal
        open={showDeleteEventModal}
        onClose={() => setShowDeleteEventModal(false)}
        onConfirm={() => { setShowDeleteEventModal(false); handleDelete(); }}
        title="Delete Event"
        description="Are you sure you want to delete this event? This action cannot be undone."
        confirmLabel="Delete"
      />

      <DeleteConfirmModal
        open={removeStepIdx !== null}
        onClose={() => setRemoveStepIdx(null)}
        onConfirm={() => { removeFollowupStep(removeStepIdx); setRemoveStepIdx(null); }}
        title="Remove Follow-Up Step"
        description="Are you sure you want to remove this follow-up step?"
        confirmLabel="Remove"
      />

      <UpgradePrompt
        open={upgradePrompt.open}
        onClose={() => setUpgradePrompt((prev) => ({ ...prev, open: false }))}
        upgradeUrl={upgradePrompt.upgradeUrl}
      />

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast((t) => ({ ...t, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <MuiAlert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {toast.message}
        </MuiAlert>
      </Snackbar>
      </Container>
    </Box>
  );
}
